import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { HERO_IMAGES } from "@/lib/constants";

const AUTOPLAY_MS = 5500;

export default function HeroSlider() {
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);

    const go = useCallback(
        (i) => setIdx(((i % HERO_IMAGES.length) + HERO_IMAGES.length) % HERO_IMAGES.length),
        [],
    );
    const next = useCallback(() => go(idx + 1), [idx, go]);
    const prev = useCallback(() => go(idx - 1), [idx, go]);

    useEffect(() => {
        if (paused) return undefined;
        const id = setInterval(next, AUTOPLAY_MS);
        return () => clearInterval(id);
    }, [next, paused]);

    return (
        <section
            className="relative w-full aspect-video overflow-hidden bg-[#1B1B1B]"
            data-testid="hero-slider"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            {HERO_IMAGES.map((src, i) => (
                <img
                    key={src}
                    src={src}
                    alt=""
                    loading={i === 0 ? "eager" : "lazy"}
                    className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1200ms] ease-in-out ${
                        i === idx ? "opacity-100" : "opacity-0"
                    }`}
                    data-testid={`hero-slide-${i}`}
                />
            ))}

            {/* Prev / next */}
            <button
                onClick={prev}
                aria-label="Previous slide"
                data-testid="hero-prev"
                className="absolute top-1/2 -translate-y-1/2 left-3 md:left-6 z-10 bg-black/40 hover:bg-[#E63B26] text-white p-2 transition"
            >
                <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" />
            </button>
            <button
                onClick={next}
                aria-label="Next slide"
                data-testid="hero-next"
                className="absolute top-1/2 -translate-y-1/2 right-3 md:right-6 z-10 bg-black/40 hover:bg-[#E63B26] text-white p-2 transition"
            >
                <ChevronRight className="h-5 w-5 md:h-6 md:w-6" />
            </button>

            {/* Dot indicators */}
            <div className="absolute bottom-6 right-6 md:bottom-10 md:right-10 flex gap-2 z-10">
                {HERO_IMAGES.map((_, i) => (
                    <button
                        key={i}
                        onClick={() => go(i)}
                        aria-label={`Go to slide ${i + 1}`}
                        data-testid={`hero-dot-${i}`}
                        className={`h-2 transition-all duration-500 ${
                            i === idx
                                ? "w-10 bg-[#F5C53D]"
                                : "w-2 bg-white/50 hover:bg-white"
                        }`}
                    />
                ))}
            </div>
        </section>
    );
}
